/*
 * Copyright (c) 2019  Apricad LLC. All Rights Reserved.
 */

// Post Reducer for posts

import { Reducer, AnyAction } from "redux";

import {
  ADD_POST,
  ADD_COMMENT,
  DELETE_POST,
  DELETE_COMMENT,
  GET_COURSE_POSTS,
  GET_COMMENTS,
  UPDATE_POSTS,
  UPDATE_COMMENTS,
  ADD_SURVEY,
  CLEAR_SURVEY,
  SURVEY_FAILED,
} from "client/actions/types";

// format of Store
//
// coursePosts: {
//   course: course_id,
//   posts: [
//     { _id, name, picture, content, dateAdded, lksCount, cmtCount, liked },
//     { _id, name, picture, content, dateAdded, lksCount, cmtCount, liked },
//   ],
//   postId: pid of the post with comments[]
//   comments: [
//     { _id, user, name, picture, content, dateAdded, lksCount, liked },
//     { _id, user, name, picture, content, dateAdded, lksCount, liked },
//   ],
// }
//

export type PostsState = {
  readonly course?: string;
  readonly posts: any[] | null;
  readonly postId?: string;
  readonly comments: any[] | null;
  readonly surveyState: string | null;
};

const postReducerDefaultState: PostsState = {
  // course, posts[] under this course
  course: undefined,
  posts: null,
  // postId, comments[] under the post with this postId
  postId: undefined,
  comments: null,
  surveyState: null,
};

export const postReducer: Reducer<PostsState, AnyAction> = (
  state = postReducerDefaultState,
  action: AnyAction,
) => {
  switch (action.type) {
    case ADD_SURVEY:
    case SURVEY_FAILED:
      return { ...state, surveyState: action.type };

    case CLEAR_SURVEY:
      return { ...state, surveyState: null };

    case GET_COURSE_POSTS:
      return { ...state, ...action.payload };

    case GET_COMMENTS:
      return { ...state, ...action.payload };

    case ADD_POST: {
      if (state.posts === null) {
        return state;
      } else {
        const post = action.payload;
        var posts = state.posts.slice(); // copy an array by value
        posts.unshift({ ...post, lksCount: 0, cmtCount: 0, liked: false });
        return { ...state, posts };
      }
    }

    case ADD_COMMENT: {
      if (state.comments === null) {
        return state;
      } else {
        const comment = action.payload;
        var comments = state.comments.slice();
        comments.push({ ...comment, lksCount: 0, liked: false });
        return { ...state, comments };
      }
    }

    case UPDATE_POSTS: {
      var posts = state.posts;
      Object.keys(action.payload).forEach((pid) => {
        const idx = posts.findIndex((p) => p._id === pid);
        if (idx !== -1) {
          Object.assign(posts[idx], action.payload[pid]);
        }
      });
      return { ...state, posts };
    }

    case UPDATE_COMMENTS: {
      var comments = state.comments;
      Object.keys(action.payload).forEach((cmtId) => {
        const idx = comments.findIndex((cmt) => cmt._id === cmtId);
        if (idx !== -1) {
          Object.assign(comments[idx], action.payload[cmtId]);
        }
      });
      return { ...state, comments };
    }

    case DELETE_POST: {
      if (!state.posts) return state;

      const { _id } = action.payload;
      const idx = state.posts.findIndex((post) => post._id === _id);
      if (idx !== -1) {
        var posts = state.posts.slice();
        posts.splice(idx, 1);
        return { ...state, posts };
      } else {
        return state;
      }
    }

    case DELETE_COMMENT: {
      if (!state.comments) return state;

      const { _id } = action.payload;
      const idx = state.comments.findIndex((post) => post._id === _id);
      if (idx !== -1) {
        var comments = state.comments.slice();
        comments.splice(idx, 1);
        return { ...state, comments };
      } else {
        return state;
      }
    }

    default:
      return state;
  }
};
